import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles, KeyRound, ShieldCheck, ShieldAlert, MailCheck, Mail,
  History, LogOut, Loader2, ArrowLeft, CheckCircle2, AlertCircle,
} from "lucide-react";
import { orgApi, saveOrganizerToken } from "../lib/organizerApi";
import { useOrganizerAuth } from "../auth/OrganizerAuthContext";
import PasswordStrengthMeter, { evaluatePassword } from "../components/PasswordStrengthMeter";
import PasswordInput from "../components/PasswordInput";

/**
 * Organizer Security & Account page — Phase 6.5 / Module 4.
 *
 * Centralises:
 *  - Email verification status + resend
 *  - Phone verification status (read-only here; user must use Setup wizard)
 *  - Change password (with strength meter)
 *  - Activity / audit log
 *  - Logout
 *  - 2FA placeholder ("Coming soon")
 */
export default function OrganizerSecurity() {
  const navigate = useNavigate();
  const { organizer, logout, refresh } = useOrganizerAuth();
  const [toast, setToast] = useState("");

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2400); };

  const onLogout = async () => {
    try { await orgApi.post("/organizer/logout"); } catch (_e) { /* ignore */ }
    logout();
    navigate("/organizer/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-linen" data-testid="organizer-security-page">
      <header className="border-b border-espresso/10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/organizer/dashboard" className="inline-flex items-center gap-1.5 text-espresso text-sm">
            <ArrowLeft className="w-4 h-4" /> Bảng điều khiển
          </Link>
          <div className="inline-flex items-center gap-2 text-espresso">
            <Sparkles className="w-4 h-4" />
            <span className="tracking-[0.28em] uppercase text-sm">Bảo mật tài khoản</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-6">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-espresso-muted">Tài khoản</p>
          <h1 className="text-3xl sm:text-4xl font-serif text-espresso mt-1">Bảo mật & Phiên đăng nhập</h1>
          <p className="mt-2 text-espresso-soft text-sm">
            Quản lý mật khẩu, xác minh email và xem nhật ký hoạt động của tài khoản.
          </p>
        </div>

        <VerificationCard organizer={organizer} onChange={refresh} onFlash={flash} />

        <ChangePasswordCard onFlash={flash} />

        <TwoFactorPlaceholderCard />

        <AuditLogCard />

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onLogout}
            data-testid="security-logout"
            className="inline-flex items-center gap-2 text-sm text-rose-700 hover:text-rose-900"
          >
            <LogOut className="w-4 h-4" /> Đăng xuất khỏi thiết bị này
          </button>
        </div>
      </main>

      {toast && (
        <div data-testid="security-toast" className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-espresso text-cream rounded-full px-4 py-2 text-sm shadow-lg z-50">
          {toast}
        </div>
      )}
    </div>
  );
}

function VerificationCard({ organizer, onChange, onFlash }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const emailVerified = !!organizer?.verified_email_at;
  const phoneVerified = !!organizer?.verified_phone_at;

  const resend = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await orgApi.post("/organizer/verify-email/send", {});
      if (data.already_verified) {
        onFlash("Email đã được xác minh");
        await onChange();
      } else {
        onFlash("Đã gửi email xác minh");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Không thể gửi email xác minh.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="security-verification-card" className="rounded-3xl bg-white border border-espresso/10 p-5 sm:p-6">
      <h2 className="text-lg font-serif text-espresso">Trạng thái xác minh</h2>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <StatusRow
          icon={emailVerified ? <MailCheck className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
          label="Email"
          value={organizer?.contact_email || "—"}
          done={emailVerified}
          testId="verify-email-status"
          actionLabel={emailVerified ? "Đã xác minh" : "Gửi lại email"}
          onAction={emailVerified ? null : resend}
          actionBusy={busy}
        />
        <StatusRow
          icon={phoneVerified ? <ShieldCheck className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
          label="Số điện thoại"
          value={organizer?.contact_phone || "—"}
          done={phoneVerified}
          testId="verify-phone-status"
          actionLabel={phoneVerified ? "Đã xác minh" : "Xác minh trong Setup"}
          onAction={null}
          actionBusy={false}
        />
      </div>
      {error && (
        <div data-testid="verify-error" className="mt-3 rounded-xl bg-rose-50 border border-rose-200 px-4 py-2.5 text-sm text-rose-700">
          {error}
        </div>
      )}
    </section>
  );
}

function StatusRow({ icon, label, value, done, testId, actionLabel, onAction, actionBusy }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border p-4 ${done ? "border-[#2D4A3E]/20 bg-[#E1EDE6]/40" : "border-espresso/10 bg-linen-100/40"}`}
    >
      <div className="flex items-center gap-2">
        <div className={done ? "text-[#2D4A3E]" : "text-espresso-muted"}>{icon}</div>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">{label}</div>
      </div>
      <div className="mt-1 text-sm font-medium text-espresso break-all">{value}</div>
      <div className="mt-3">
        {onAction ? (
          <button
            type="button"
            onClick={onAction}
            disabled={actionBusy}
            data-testid={`${testId}-action`}
            className="inline-flex items-center justify-center gap-1.5 bg-espresso text-cream rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.18em] font-semibold disabled:opacity-60"
          >
            {actionBusy && <Loader2 className="w-3 h-3 animate-spin" />}
            {actionLabel}
          </button>
        ) : (
          <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold ${done ? "text-[#2D4A3E]" : "text-espresso-muted"}`}>
            {done ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
            {actionLabel}
          </div>
        )}
      </div>
    </div>
  );
}

function ChangePasswordCard({ onFlash }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const strength = evaluatePassword(next);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) { setError("Mật khẩu mới phải có ít nhất 8 ký tự."); return; }
    if (next !== confirm) { setError("Hai mật khẩu mới không khớp."); return; }
    if (!strength.ok) { setError("Mật khẩu mới quá yếu. Hãy dùng hỗn hợp chữ + số + ký tự."); return; }
    if (next === current) { setError("Mật khẩu mới phải khác mật khẩu hiện tại."); return; }

    setBusy(true);
    try {
      const { data } = await orgApi.post("/organizer/change-password", {
        current_password: current,
        new_password: next,
      });
      if (data.access_token) saveOrganizerToken(data.access_token);
      onFlash("Đã đổi mật khẩu. Các thiết bị khác đã bị thu hồi.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) setError("Mật khẩu hiện tại không đúng.");
      else setError(typeof detail === "string" ? detail : "Không thể đổi mật khẩu.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="security-change-password-card" className="rounded-3xl bg-white border border-espresso/10 p-5 sm:p-6">
      <div className="flex items-center gap-2">
        <KeyRound className="w-4 h-4 text-espresso-soft" />
        <h2 className="text-lg font-serif text-espresso">Đổi mật khẩu</h2>
      </div>
      <p className="mt-1 text-xs text-espresso-soft">
        Sau khi đổi, mọi phiên đăng nhập khác sẽ bị thu hồi và phải đăng nhập lại.
      </p>
      <form onSubmit={submit} className="mt-4 grid sm:grid-cols-3 gap-3">
        <Input
          label="Mật khẩu hiện tại"
          value={current}
          onChange={setCurrent}
          testId="pw-current"
          autoComplete="current-password"
        />
        <div className="sm:col-span-1">
          <Input
            label="Mật khẩu mới"
            value={next}
            onChange={setNext}
            testId="pw-new"
            autoComplete="new-password"
          />
          {next && <PasswordStrengthMeter password={next} testIdPrefix="pw-new-strength" />}
        </div>
        <Input
          label="Nhập lại mật khẩu mới"
          value={confirm}
          onChange={setConfirm}
          testId="pw-confirm"
          autoComplete="new-password"
        />
        {error && (
          <div data-testid="pw-error" className="sm:col-span-3 rounded-xl bg-rose-50 border border-rose-200 px-4 py-2.5 text-sm text-rose-700">
            {error}
          </div>
        )}
        <div className="sm:col-span-3 flex justify-end">
          <button
            type="submit"
            disabled={busy || !current || !next || !confirm}
            data-testid="pw-submit"
            className="inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            {busy ? "Đang lưu…" : "Cập nhật mật khẩu"}
          </button>
        </div>
      </form>
    </section>
  );
}

function Input({ label, value, onChange, testId, autoComplete }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">{label}</span>
      <PasswordInput
        data-testid={testId}
        testIdSuffix={testId || "security-password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        minLength={8}
        autoComplete={autoComplete}
        wrapperClassName="mt-1"
        leftIcon={null}
        className="w-full bg-linen-100/60 border border-espresso/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso"
      />
    </label>
  );
}

function TwoFactorPlaceholderCard() {
  return (
    <section data-testid="security-2fa-card" className="rounded-3xl bg-white border border-espresso/10 p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-espresso-soft" />
            <h2 className="text-lg font-serif text-espresso">Xác thực hai yếu tố (2FA)</h2>
          </div>
          <p className="mt-1 text-xs text-espresso-soft">
            Thêm lớp bảo vệ bằng ứng dụng xác thực hoặc mã SMS. Tính năng đang được phát triển.
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-[0.22em] font-semibold bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full">
          Sắp ra mắt
        </span>
      </div>
    </section>
  );
}

const ACTION_LABELS = {
  login: { vi: "Đăng nhập thành công",        color: "text-emerald-700" },
  login_failed: { vi: "Đăng nhập thất bại",   color: "text-rose-700" },
  login_locked: { vi: "Tài khoản bị khoá",     color: "text-rose-700" },
  logout: { vi: "Đăng xuất",                   color: "text-espresso-soft" },
  password_change: { vi: "Đổi mật khẩu",       color: "text-amber-700" },
  password_reset_requested: { vi: "Yêu cầu đặt lại mật khẩu", color: "text-amber-700" },
  password_reset_completed: { vi: "Đã đặt lại mật khẩu",      color: "text-emerald-700" },
  email_verify_sent: { vi: "Gửi email xác minh",              color: "text-espresso" },
  email_verified: { vi: "Email đã xác minh",                  color: "text-emerald-700" },
  signup: { vi: "Tạo tài khoản",                              color: "text-emerald-700" },
};

function AuditLogCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await orgApi.get("/organizer/audit-log", { params: { limit: 30 } });
      setRows(data.items || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Không thể tải nhật ký.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <section data-testid="security-audit-card" className="rounded-3xl bg-white border border-espresso/10 p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-espresso-soft" />
          <h2 className="text-lg font-serif text-espresso">Nhật ký hoạt động</h2>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="audit-refresh"
          className="text-xs uppercase tracking-[0.18em] text-espresso-soft hover:text-espresso"
        >
          Làm mới
        </button>
      </div>

      {loading ? (
        <div className="py-8 text-center text-espresso-soft text-sm" data-testid="audit-loading">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Đang tải…
        </div>
      ) : error ? (
        <div data-testid="audit-error" className="mt-3 rounded-xl bg-rose-50 border border-rose-200 px-4 py-2.5 text-sm text-rose-700">
          {error}
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="audit-empty" className="py-8 text-center text-espresso-muted text-sm">
          Chưa có hoạt động nào được ghi nhận.
        </div>
      ) : (
        <ul className="mt-3 divide-y divide-espresso/10" data-testid="audit-list">
          {rows.map((r) => {
            const meta = ACTION_LABELS[r.action] || { vi: r.action, color: "text-espresso" };
            return (
              <li key={r.id} data-testid={`audit-row-${r.id}`} className="py-2.5 flex items-center gap-3 text-sm">
                <span className={`font-semibold ${meta.color}`}>{meta.vi}</span>
                {!r.success && <span className="text-[10px] uppercase tracking-[0.18em] font-semibold bg-rose-100 text-rose-800 px-2 py-0.5 rounded-full">Thất bại</span>}
                <span className="ml-auto text-xs text-espresso-muted whitespace-nowrap">
                  {r.created_at ? new Date(r.created_at).toLocaleString("vi-VN") : "—"}
                </span>
                <span className="text-xs text-espresso-muted font-mono hidden sm:inline">{r.ip_address || ""}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
